/** @module csi/heading/SelectController */
define([
	"dojo/_base/declare",
	"dojo/keys",
	"mobx",
	"underscore",
	"csi",
	"csi/state/rootStore",
	"./InlineController"
], function(declare, keys, {runInAction}, _, csi, {localStore}, InlineController) {

	return declare(InlineController, {
		/** @property {boolean} delaySearch - indicate if search for selections should be delayed until minimum number of characters */
		delaySearch: false,

		/** @property {string} oldDisplayedValue - name of existing value */
		oldDisplayedValue: false,

		getDisplayDelay: function() {
			//summary: delayed rendering of controls
			if (this.editNode && this.editNode.displayDelay) {
				return this.editNode.displayDelay;
			} else {
				return 0;
			}
		},

		_fromHtmlDynamic: function() {
			// summary: extract new value from edit node; if no item was selected, then assume no change to value
			if (this.editNode && this.editNode.widgetType == "multiSelect") {
				return !this.editNode.get("aborted") ? this.ren.fromHtmlDynamic(this._ctx, this.heading, this.editNode) : this.oldValue;
			} else {
				return this.editNode.item ? this.ren.fromHtmlDynamic(this.getContext(), this.heading, this.editNode) : this.oldValue;
			}
		},

		_getEditNodeStyles: function(/*Number*/w, /*Number*/h) {
			// summary: set minimum width of widget up to a certain limit - if cell is really wide, the widget does not need to match it
			return {
				minWidth: Math.max(125, Math.min(w, 250)) + "px",
				width: "auto"
			};
		},

		getOldValue: function() {
			// summary: return previous value for attribute
			// fix: for old value getting lost when arrays gets manipulated
			var v = this.inherited(arguments);
			if (_.isArray(v)) {
				v = [].concat(v);
			}
			return v;
		},

		_isValueChanged: function() {
			// summary: convert back to single string for comparison
			return !_.isEqual(_.isArray(this.newValue) ? [].concat(this.newValue).sort() : this.newValue,
				_.isArray(this.oldValue) ? [].concat(this.oldValue).sort() : this.oldValue);
		},

		_processTabOrEnter: function(/*Event*/evt) {
			// summary: handle keypress of tab or enter if widget is valid
			const {delaySearch, editNode} = this;

			if (this._bProcessing || !this.isValidAction(true)) {
				return true;
			}

			if (editNode) {
				const {dropDown, store, widgetType, _opened} = editNode;
				const multiSelect = widgetType == "multiSelect";
				const displayedValue = editNode.get("displayedValue");
				const item = editNode.get("item");

				if (multiSelect && _opened && evt.keyCode === keys.ENTER) {
					// Do not process ENTER events from multi-select widget if dropdown is open. 
					// Allow to navigate to next control on ENTER if drop down is closed
					// FIX: this should be handled by widget rather than controller so that this event never gets to _onInput (reference TimeTextBox)
					return false;
				} else if (!multiSelect && (store.getIdentity(item) == null)) {
					// if any item is highlighted, including More/Previous choices, then use default behavior
					const highlighted = dropDown && dropDown.getHighlightedOption();
					if (highlighted) {
						return false;
					}

					// check that item is selected when enter hit before processing, or if no input has been
					// entered, then it is okay to continue on to next cell
					if (displayedValue) {
						// if ENTER pressed and there is text entered when delaying search, then instruct store to search immediately on next query;
						// otherwise, there are multiple matches and so need to wait for user to pick one
						if (delaySearch) {
							store.skipDelayOnNextQuery = true;
							editNode._startSearch(displayedValue);
						}

						// cancel default behavior which is to close drop down and validate (validate is not so bad, but closing drop drown is not desired)
						evt.preventDefault();
						return false;
					} else {
						// validate widget before setting back to old value
						if (editNode.validate && !editNode.validate()) {
							return false;
						}

						this._keyCode = evt.keyCode;
						this._keyShift = evt.shiftKey;

						if (delaySearch) {
							// if ENTER pressed with nothing entered, then clear any existing value
							this._processEmptyValue();
						} else {
							// if ENTER pressed with nothing entered, then keep existing value
							this._processNewValue(this.oldValue);
						}

						evt.preventDefault();
						return true;
					}
				}
			}

			return this.inherited(arguments);
		},

		/**
		 * If there was an existing value, then open confirmation dialog indicating previous value will be cleared
		 * (unless confirmation dialog has been dismissed for good)
		 */
		_processEmptyValue() {
			const {editNode, oldDisplayedValue, oldValue} = this;

			this._bProcessing = true;

			if (localStore.dismissConfirmationDialogOnClearSelection) {
				this._processNewValue(csi.EMPTY_URL);
			} else {
				// if opening dialog, then do not continue to next cell when done and clear reset values
				// so drop-down will still show an empty value when drop down is closed
				this._keyCode = null;
				editNode._csiResetValue = editNode._resetValue = csi.EMPTY_URL;
				editNode.closeDropDown();

				editNode.showConfirmOnClearSelectionDialog(
					oldValue,
					oldDisplayedValue,
					(shouldDismiss) => (
						runInAction(() => {
							localStore.dismissConfirmationDialogOnClearSelection = shouldDismiss;
						})
					),
					(value) => this._processNewValue(value)
				);
			}
		},

		/**
		 * Start processing with given value
		 * @param {string} value
		 */
		_processNewValue(value) {
			this._bProcessing = true;
			this.newValue = value;
			this._processValue();
		},

		_toHtmlDynamic: function(/*Context*/ctx, /*Object*/params) {
			// summary: create edit widget
			var editNode = this.inherited(arguments);

			// connect to events
			this._connectEvent(editNode, "onBlur", "_onProcess");
			this._connectEvent(editNode, "onInput", "_onInput");
			this._connectEvent(editNode, "onSelectOption", "_onProcess");

			return editNode;
		}
	});
});